// Wave F4 NAMEPLATE, widened by VOCAB-1. The merchant vocabulary layer: the
// core nouns a business can rename (each a {one, many} pair), plus job-status
// label overrides. Everything resolves against built-in defaults, so an absent
// or partial config renders TODAY'S exact words: every surface is
// byte-identical until a merchant sets a word.
//
// VOCAB-1 WIDENED THIS FROM FOUR NOUNS TO TWELVE and bound the labels that
// name them. F4 shipped four nouns (job, quote, tech, visit) consumed at four
// choke points, which left a merchant who renamed "Customers" to "Clients"
// looking at "Customers" in the menu, the page title and every heading.
//
// THE STANDING RULE THIS FILE OBEYS: BEHAVIOUR NEVER COMPARES AGAINST A WORD A
// BUSINESS CAN RENAME. A noun key here is IDENTITY and never changes; the word
// is DISPLAY and changes freely. Nothing branches on a word. The same rule
// governs job status, whose MEANING lives in jobStatusMeanings.ts as flags -
// the label is here, and the two are deliberately in different files so that
// reading a label can never be mistaken for reading a meaning.

export type VocabNoun =
  | 'job'
  | 'quote'
  | 'customer'
  | 'lead'
  | 'opportunity'
  | 'invoice'
  | 'task'
  | 'tech'
  | 'crew'
  | 'visit'
  | 'pipeline'
  | 'stage'

export interface NounForms {
  one: string
  many: string
}

/** The seven job status codes (src/types/database.ts JobStatus). Codes are
 *  workflow semantics and never change; only their labels ride the vocabulary.
 *  What each code MEANS is jobStatusMeanings.ts, not this file. */
export type JobStatusCode =
  | 'scheduled'
  | 'en_route'
  | 'in_progress'
  | 'completed'
  | 'cancelled'
  | 'no_show'
  | 'rescheduled'

/** The stored blob (vocabulary_settings.words, and the portal-fetch payload's
 *  `vocabulary`). Every field optional — a partial override merges onto the
 *  defaults, so a blank/missing field always falls back to today's word. */
export type VocabularyWords = {
  [K in VocabNoun]?: Partial<NounForms>
} & {
  job_status?: Partial<Record<JobStatusCode, string>>
}

/** A fully-resolved vocabulary: every noun and status has a concrete word. */
export interface Vocabulary {
  nouns: Record<VocabNoun, NounForms>
  jobStatus: Record<JobStatusCode, string>
}

/**
 * THE TWELVE, AND WHY THESE.
 *
 * A noun earns a place here when it satisfies BOTH tests:
 *   (a) it names a RECORD A PERSON ACTS ON - something with its own screen,
 *       its own list and its own lifecycle; and
 *   (b) it is READ BY A PERSON in navigation, a page title or a heading, so
 *       renaming it without binding the label would leave the merchant's word
 *       and ours on screen at the same time.
 *
 * DELIBERATELY EXCLUDED, so the list is a decision and not an accident:
 *   - estimate, bid, proposal - these are ALTERNATIVE WORDS FOR quote, which
 *     is the point of the feature. Adding them would let a business set two
 *     different words for one record.
 *   - appointment, service call - same, for visit.
 *   - payment, refund, payout, fee - MONEY ARITHMETIC. GHOST-VISION names it
 *     as one of the four things that are never customisable, and a business
 *     renaming "refund" is how a dispute becomes unreadable.
 *   - message, conversation, review - not a record with a lifecycle a business
 *     shapes; and messaging law is likewise never customisable.
 *   - team member, role, permission - these name the PERMISSION SYSTEM, and a
 *     renamed role in an access-control screen is a safety problem, not a
 *     preference. `tech` and `crew` are the work-facing nouns and are here.
 */
export const VOCAB_NOUNS: VocabNoun[] = [
  'job',
  'quote',
  'customer',
  'lead',
  'opportunity',
  'invoice',
  'task',
  'tech',
  'crew',
  'visit',
  'pipeline',
  'stage',
]

/** Built-in noun words — byte-identical to today's hardcoded strings. */
export const DEFAULT_NOUNS: Record<VocabNoun, NounForms> = {
  job: { one: 'job', many: 'jobs' },
  quote: { one: 'quote', many: 'quotes' },
  customer: { one: 'customer', many: 'customers' },
  lead: { one: 'lead', many: 'leads' },
  opportunity: { one: 'opportunity', many: 'opportunities' },
  invoice: { one: 'invoice', many: 'invoices' },
  task: { one: 'task', many: 'tasks' },
  tech: { one: 'technician', many: 'technicians' },
  crew: { one: 'crew', many: 'crews' },
  visit: { one: 'visit', many: 'visits' },
  pipeline: { one: 'pipeline', many: 'pipelines' },
  stage: { one: 'stage', many: 'stages' },
}

/** Built-in job-status labels — byte-identical to the maps in Jobs.tsx /
 *  JobDetail.tsx / CrewLaneView.tsx today (note: en_route reads "On the way"
 *  on the jobs enum; the Dispatch "En route" is a separate assignment status). */
export const DEFAULT_JOB_STATUS_LABELS: Record<JobStatusCode, string> = {
  scheduled: 'Scheduled',
  en_route: 'On the way',
  in_progress: 'In progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
  no_show: 'No show',
  rescheduled: 'Rescheduled',
}

export const DEFAULT_VOCABULARY: Vocabulary = {
  nouns: DEFAULT_NOUNS,
  jobStatus: DEFAULT_JOB_STATUS_LABELS,
}

/** A single-noun label ordering for the nouns (settings editor + tests). */
export const VOCAB_NOUN_META: Record<VocabNoun, { label: string; hint: string }> = {
  job: { label: 'Job', hint: 'A booked piece of work. Alt: service call, visit, appointment.' },
  quote: {
    label: 'Quote',
    hint: 'A price you send to win the work. Alt: estimate, proposal, bid.',
  },
  customer: {
    label: 'Customer',
    hint: 'Someone who has bought from you. Alt: client, account, member, patient.',
  },
  lead: {
    label: 'Lead',
    hint: 'Someone who might buy, before they have. Alt: enquiry, prospect, contact.',
  },
  opportunity: {
    label: 'Opportunity',
    hint: 'A live chance to win work, sitting on a pipeline. Alt: deal, project, matter.',
  },
  invoice: {
    label: 'Invoice',
    hint: 'A request for payment. Alt: bill, statement.',
  },
  task: { label: 'Task', hint: 'Something one person has to do. Alt: to-do, action, ticket.' },
  tech: {
    label: 'Technician',
    hint: 'The person who does the work. Alt: crew, cleaner, installer, pro, stylist.',
  },
  crew: {
    label: 'Crew',
    hint: 'A group who work together. Alt: team, squad, van, shift.',
  },
  visit: { label: 'Visit', hint: 'A customer-facing appointment. Alt: appointment, service.' },
  pipeline: {
    label: 'Pipeline',
    hint: 'The ordered path work moves along. Alt: board, funnel, process.',
  },
  stage: {
    label: 'Stage',
    hint: 'One step on that path. Alt: step, column, status.',
  },
}

function cleanWord(v: unknown): string | null {
  return typeof v === 'string' && v.trim() ? v.trim() : null
}

function mergeForms(base: NounForms, override?: Partial<NounForms>): NounForms {
  if (!override || typeof override !== 'object') return base
  return {
    one: cleanWord(override.one) ?? base.one,
    many: cleanWord(override.many) ?? base.many,
  }
}

/**
 * Merge a stored words blob (possibly null / partial / malformed) onto the
 * defaults. Any missing or blank field resolves to today's word, so the result
 * is byte-identical to the built-ins when the blob is empty or absent.
 *
 * DERIVED FROM VOCAB_NOUNS, not written out noun by noun: adding a thirteenth
 * noun to the list above is the whole change, and a noun can never be added to
 * the type and silently left unresolvable here.
 */
export function resolveVocabulary(words?: VocabularyWords | null): Vocabulary {
  if (!words || typeof words !== 'object') return DEFAULT_VOCABULARY
  const nouns = {} as Record<VocabNoun, NounForms>
  for (const noun of VOCAB_NOUNS) {
    nouns[noun] = mergeForms(DEFAULT_NOUNS[noun], words[noun])
  }
  const jobStatus: Record<JobStatusCode, string> = { ...DEFAULT_JOB_STATUS_LABELS }
  if (words.job_status && typeof words.job_status === 'object') {
    for (const code of Object.keys(DEFAULT_JOB_STATUS_LABELS) as JobStatusCode[]) {
      const override = cleanWord(words.job_status[code])
      if (override) jobStatus[code] = override
    }
  }
  return { nouns, jobStatus }
}

function capitalize(s: string): string {
  return s.length ? s[0].toUpperCase() + s.slice(1) : s
}

export interface WordOpts {
  /** Use the plural form (many) instead of the singular (one). */
  plural?: boolean
  /** Capitalize the first letter (for labels/titles). */
  capital?: boolean
}

/** The merchant's word for a noun. `plural` picks `many`; `capital` title-cases. */
export function vocabWord(vocab: Vocabulary, noun: VocabNoun, opts: WordOpts = {}): string {
  const forms = vocab.nouns[noun]
  const word = opts.plural ? forms.many : forms.one
  return opts.capital ? capitalize(word) : word
}

/** The merchant's label for a job status. The status CODE stays semantic-fixed;
 *  an unknown code falls through to the raw code (never throws).
 *
 *  THIS IS A LABEL AND NOTHING MAY BRANCH ON IT. What a status MEANS is
 *  jobStatusMeanings.ts. */
export function jobStatusLabel(vocab: Vocabulary, status: string): string {
  return vocab.jobStatus[status as JobStatusCode] ?? status
}

// ─── The three states ──────────────────────────────────────────────────
//
// GHOST-VISION law 2, at the vocabulary. Before VOCAB-1 a failed read and an
// unconfigured account produced the SAME VALUE - DEFAULT_VOCABULARY - so a
// merchant whose words could not be read saw our words with no indication
// that theirs had been asked for and not found. Renaming is invisible when it
// works, which is exactly why its failure has to be visible.
//
// Falling back to the DEFAULTS on failure is deliberate and is the safe
// failure: blank labels would leave a merchant looking at an unusable menu.
// The warning is what makes it honest rather than silent.

export type VocabularyState =
  | { state: 'loading' }
  | { state: 'failed'; reason: string }
  | { state: 'loaded'; words: VocabularyWords | null }

export interface ResolvedVocabulary {
  vocab: Vocabulary
  /** Non-null only when the words could not be read. */
  warning: string | null
  /** True only for 'loaded' — the merchant's own words are on screen. */
  loaded: boolean
  /** True when we read successfully and the business had set nothing. */
  usingDefaults: boolean
}

export function resolveVocabularyState(state: VocabularyState): ResolvedVocabulary {
  if (state.state === 'loaded') {
    const hasWords = !!state.words && Object.keys(state.words).length > 0
    return {
      vocab: resolveVocabulary(state.words),
      warning: null,
      loaded: true,
      usingDefaults: !hasWords,
    }
  }
  if (state.state === 'failed') {
    return {
      vocab: DEFAULT_VOCABULARY,
      warning: `We could not read the words your business uses, so these are the standard ones. ${state.reason}`,
      loaded: false,
      usingDefaults: true,
    }
  }
  return { vocab: DEFAULT_VOCABULARY, warning: null, loaded: false, usingDefaults: true }
}
